import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import {
  type DragEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "../components/ui/button";
import { type ApiError, apiGet } from "../lib/api";
import { cn } from "../lib/utils";
import { type RenduReponse, type SilhouettesInfo, silhouettesInfoSchema } from "./contract";
import { useLiensDeFichiers } from "./silhouettes-fichiers";
import { ErreurImage, type ImageReduite, reduireImage } from "./silhouettes-image";
import {
  appliquer,
  CarteReglages,
  dejaApplique,
  lireSaisie,
  type Saisie,
  saisieDepuis,
} from "./silhouettes-reglages";
import { type ImageEnvoi, type Rendu, useRendu } from "./silhouettes-rendu";
import {
  bilan,
  decrireImage,
  filaments,
  nettoyerNomFichier,
  nomDepuisFichier,
  puces,
  pucesCourtes,
  type Segment,
  titreFilaments,
  traduire,
  traduireErreur,
} from "./silhouettes-textes";

/**
 * Le module Silhouettes : l'outil serre-livres sans terminal (spec du 21 septembre 2026,
 * issue #145, maquettes `docs/maquettes/console-lab/Silhouettes*.dc.html`). Deposer une
 * image, regler, voir l'apercu se refaire tout seul, telecharger le 3MF.
 *
 * **Rien n'est garde**, ni ici ni sur fortress : l'image reste dans la page, les fichiers
 * arrivent dans la reponse et deviennent des liens locaux (`Blob`), revoques a chaque
 * nouvelle reponse.
 *
 * Mise en page : le DOM est dans l'ordre du telephone et du travail, Image, Apercu, Fichiers,
 * Reglages ; c'est lui que suivent le clavier et le lecteur d'ecran, a toutes les largeurs.
 * A partir de `lg` (1 024 px), `grid-template-areas` place Image puis Reglages a gauche sur
 * 380 px, et Apercu puis Fichiers a droite, sans `order`. Pas a `md` : de 768 a 1 023 px, le
 * menu de la coquille prend deja 13rem, et l'apercu n'aurait qu'une centaine de pixels.
 *
 * La page elle-meme (le titre, le choix entre cette page et le mode serre-livres) est dans
 * `silhouettes-page.tsx` : ce fichier n'importe pas le mode serre-livres, qui lui importe ses cartes.
 */

export function useSilhouettesInfo(): UseQueryResult<SilhouettesInfo, Error> {
  return useQuery({
    queryKey: ["silhouettes"],
    queryFn: () => apiGet("/api/silhouettes", silhouettesInfoSchema),
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export type ImageChoisie = ImageReduite & { nomFichier: string; fichier: File };

export function Atelier({
  infos,
  surRefus,
}: {
  infos: SilhouettesInfo;
  surRefus: (erreur: ApiError) => void;
}) {
  const [image, setImage] = useState<ImageChoisie | null>(null);
  const [erreurImage, setErreurImage] = useState<string | null>(null);
  const [preparation, setPreparation] = useState(false);
  const [saisie, setSaisie] = useState<Saisie>(() => saisieDepuis(infos.reglages));
  const lecture = useMemo(() => lireSaisie(saisie), [saisie]);
  const envoi = useMemo<ImageEnvoi | null>(
    () =>
      image ? { base64: image.base64, nomFichier: nettoyerNomFichier(image.nomFichier) } : null,
    [image],
  );
  const rendu = useRendu(envoi, lecture.reglages, lecture.valides);
  const changerImage = useRef<HTMLButtonElement>(null);
  // Le numero du dernier fichier choisi : une reduction plus lente qu'une autre, choisie
  // apres, ne doit pas la remplacer en finissant.
  const dernierChoix = useRef(0);
  const liens = useLiensDeFichiers(rendu.reponse);

  // Le bouton qui ouvrait le choix du fichier disparait avec la zone de depot : le focus passe
  // a « Changer d'image », dans la meme carte, plutot que de retomber sur la page.
  useEffect(() => {
    if (image) changerImage.current?.focus();
  }, [image]);

  useEffect(() => {
    const statut = rendu.erreur?.status;
    if (rendu.erreur && (statut === 401 || statut === 403)) surRefus(rendu.erreur);
  }, [rendu.erreur, surRefus]);

  const choisir = async (fichier: File): Promise<void> => {
    dernierChoix.current += 1;
    const choix = dernierChoix.current;
    setPreparation(true);
    try {
      const reduite = await reduireImage(fichier);
      if (choix !== dernierChoix.current) return;
      setErreurImage(null);
      // La nouvelle image et son nom partent ensemble : une seule demande de rendu. Les couleurs a
      // epaissir (#397) repartent vides : une autre image a d'autres couleurs, et les noms de la
      // precedente ne valent pas pour elle.
      setImage({ ...reduite, nomFichier: fichier.name, fichier });
      setSaisie((actuelle) => ({
        ...actuelle,
        nom: nomDepuisFichier(fichier.name),
        couleursEpaissies: [],
      }));
    } catch (erreur) {
      if (choix !== dernierChoix.current) return;
      // L'image precedente reste, s'il y en avait une : rien n'est perdu.
      setErreurImage(
        erreur instanceof ErreurImage ? erreur.message : `${fichier.name} n'a pas pu être lue.`,
      );
    } finally {
      if (choix === dernierChoix.current) setPreparation(false);
    }
  };

  // `rendu.reponse` ne vaut que pour l'image courante : son rapport ne decrit jamais une autre.
  const rapport = rendu.reponse?.rapport ?? null;
  // Les fichiers ne s'offrent que s'ils repondent aux reglages affiches.
  const aJour = rendu.etat === "pret" && lecture.valides && rendu.reponse !== null;

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[380px_minmax(0,1fr)] lg:grid-rows-[auto_1fr] lg:items-start lg:[grid-template-areas:'image_droite'_'reglages_droite']">
      <CarteImage
        boutonChanger={changerImage}
        choisir={(fichier) => void choisir(fichier)}
        className="lg:[grid-area:image]"
        erreur={erreurImage}
        image={image}
        origine={rapport?.origine_du_masque}
        preparation={preparation}
      />
      <div className="flex min-w-0 flex-col gap-5 lg:[grid-area:droite]">
        <CarteApercu
          appliquer={(reglage) => setSaisie((actuelle) => appliquer(actuelle, reglage))}
          lienApercu={liens.apercu}
          rendu={rendu}
          saisie={saisie}
        />
        <CarteFichiers aJour={aJour} liens={liens} reponse={rendu.reponse} />
      </div>
      <CarteReglages
        avecImage={image !== null}
        changer={setSaisie}
        className="lg:[grid-area:reglages]"
        rapport={rapport}
        saisie={saisie}
      />
    </div>
  );
}

/** Le titre d'une carte, a la taille de la maquette. */
export function TitreCarte({ id, children }: { id: string; children: string }) {
  return (
    <h2 className="text-[15px] font-semibold" id={id}>
      {children}
    </h2>
  );
}

/**
 * Les fleches de la maquette, en caracteres de texte : sans le selecteur de variante
 * U+FE0E, Windows les dessine en emoji, dans une case bleue.
 */
const FLECHE_HAUT = String.fromCodePoint(0x2b06, 0xfe0e);
const FLECHE_BAS = String.fromCodePoint(0x2b07, 0xfe0e);

export const CARTE = "min-w-0 rounded-lg border border-border bg-card p-5 text-card-foreground";
export const NOTE = "border-l-[3px] border-border pl-3 text-sm text-muted-foreground";

/** Le gros bouton de contour, celui de la maquette (« Choisir un fichier », « Changer d'image »). */
export const BOUTON_SECONDAIRE =
  "inline-flex items-center justify-center gap-2 rounded-md border border-input bg-card px-3.5 text-sm font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card";

/** Vrai quand un glisser porte des fichiers (et non du texte ou un lien). */
function porteDesFichiers(event: { dataTransfer: DataTransfer | null }): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes("Files");
}

/**
 * La zone de depot de l'image. Dans le mode serre-livres (#337), `integree` la pose dans la colonne
 * d'une figurine : ni la carte ni son titre (la colonne a les siens), un nom `etiquette` pour le
 * lecteur d'ecran, et des boutons de 44 px de haut au telephone. Rien ne change pour le mode d'une
 * silhouette.
 */
export function CarteImage({
  image,
  erreur,
  origine,
  preparation,
  choisir,
  boutonChanger,
  className,
  integree = false,
  etiquette,
}: {
  image: ImageChoisie | null;
  erreur: string | null;
  origine: string | undefined;
  preparation: boolean;
  choisir: (fichier: File) => void;
  boutonChanger: RefObject<HTMLButtonElement | null>;
  className: string;
  integree?: boolean;
  etiquette?: string;
}) {
  const id = useId();
  const champ = useRef<HTMLInputElement>(null);
  const [survol, setSurvol] = useState(false);
  const vignette = useUrlLocale(image?.fichier ?? null);

  // Une image lachee a cote de la carte ouvrirait le fichier dans l'onglet, et la page serait
  // perdue avec ses reglages. Tant que la page est montee, un glisser de fichiers est retenu
  // partout ailleurs (la carte, elle, l'a deja accepte : `defaultPrevented`).
  useEffect(() => {
    const retenir = (event: globalThis.DragEvent): void => {
      if (!porteDesFichiers(event) || event.defaultPrevented) return;
      event.preventDefault();
      if (event.type === "dragover" && event.dataTransfer) event.dataTransfer.dropEffect = "none";
    };
    document.addEventListener("dragover", retenir);
    document.addEventListener("drop", retenir);
    return () => {
      document.removeEventListener("dragover", retenir);
      document.removeEventListener("drop", retenir);
    };
  }, []);

  const deposer = (event: DragEvent<HTMLElement>): void => {
    event.preventDefault();
    setSurvol(false);
    const fichier = event.dataTransfer.files[0];
    if (fichier) choisir(fichier);
  };

  return (
    <section
      aria-label={integree ? etiquette : undefined}
      aria-labelledby={integree ? undefined : `${id}-titre`}
      className={cn(integree ? "min-w-0" : CARTE, className)}
      onDragLeave={() => setSurvol(false)}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        setSurvol(true);
      }}
      onDrop={deposer}
    >
      {integree ? null : (
        <div className="mb-3.5">
          <TitreCarte id={`${id}-titre`}>Image</TitreCarte>
        </div>
      )}
      {/* Le champ de fichier est cache : ce sont les boutons de la carte qui l'ouvrent. */}
      <input
        accept="image/png,image/jpeg,image/webp"
        aria-label="Choisir un fichier"
        className="hidden"
        onChange={(event) => {
          const fichier = event.target.files?.[0];
          // Vide, pour que choisir le meme fichier une seconde fois le relise.
          event.target.value = "";
          if (fichier) choisir(fichier);
        }}
        ref={champ}
        type="file"
      />

      {image ? (
        <div
          className={cn(
            "flex items-center gap-4 rounded-lg border-[1.5px] border-dashed p-3.5",
            integree && "md:gap-3 md:p-2.5",
            survol ? "border-foreground bg-muted" : "border-input bg-muted/40",
          )}
        >
          {vignette ? (
            <img
              alt=""
              className={cn(
                "damier w-[72px] shrink-0 rounded-md",
                integree ? "md:w-20" : "md:w-24",
              )}
              height={image.hauteur}
              src={vignette}
              width={image.largeur}
            />
          ) : null}
          <div
            className={cn(
              "min-w-0",
              // Au bureau, dans une colonne : le nom et sa description a gauche, le bouton a droite.
              integree && "md:flex md:flex-1 md:items-center md:justify-between md:gap-3",
            )}
          >
            {integree ? (
              <div className="min-w-0">
                <p className="truncate font-medium">{image.nomFichier}</p>
                <p className="text-[13px] text-muted-foreground">
                  {decrireImage(image.largeur, image.hauteur, image.octets, origine)}
                </p>
              </div>
            ) : (
              <>
                <p className="truncate font-medium">{image.nomFichier}</p>
                <p className="text-[13px] text-muted-foreground">
                  {decrireImage(image.largeur, image.hauteur, image.octets, origine)}
                </p>
              </>
            )}
            <button
              className={cn(
                BOUTON_SECONDAIRE,
                "mt-2 text-[13px]",
                integree ? "min-h-11 px-3 md:mt-0 md:min-h-8 md:shrink-0 md:px-2.5" : "h-8 px-2.5",
              )}
              onClick={() => champ.current?.click()}
              ref={boutonChanger}
              type="button"
            >
              Changer d'image
            </button>
          </div>
        </div>
      ) : (
        <div
          className={cn(
            "rounded-lg border-[1.5px] border-dashed px-5 py-9 text-center",
            survol ? "border-foreground bg-muted" : "border-input bg-muted/40",
          )}
        >
          <p aria-hidden="true" className="text-[28px] leading-none">
            {FLECHE_HAUT}
          </p>
          <p className="mt-1.5 font-medium">Dépose une image ici</p>
          <p className="mt-1 mb-3 text-[13px] text-muted-foreground">ou</p>
          <button
            className={cn(BOUTON_SECONDAIRE, integree ? "min-h-11 md:min-h-9" : "h-9")}
            onClick={() => champ.current?.click()}
            type="button"
          >
            Choisir un fichier
          </button>
          <p className="mt-3.5 text-[13px] text-muted-foreground">
            PNG, JPEG ou WebP, 10 Mo au plus
          </p>
        </div>
      )}

      {/* Toujours la, pour que le lecteur d'ecran annonce le debut de la preparation. */}
      <p
        className={cn("text-[13px] text-muted-foreground", preparation ? "mt-3.5" : "sr-only")}
        role="status"
      >
        {preparation ? "Préparation de l'image…" : ""}
      </p>
      {erreur && !preparation ? (
        <p className={cn(ALERTE.rouge, "mt-3.5")} role="alert">
          {erreur}
        </p>
      ) : null}
      {image ? null : (
        <p className={cn(NOTE, "mt-3.5")}>
          Le mieux : un PNG détouré, fond transparent. Un dessin sur fond blanc marche aussi : ce
          qu'il entoure (un gant blanc) est rempli.
        </p>
      )}
    </section>
  );
}

/** Les trois encadres de la maquette : avertissement, refus, panne. */
export const ALERTE = {
  jaune: "rounded-md border border-avertissement/60 bg-avertissement/15 px-3.5 py-2.5 text-sm",
  rouge:
    "rounded-md border border-destructive/40 bg-destructive/[0.06] px-3.5 py-2.5 text-sm text-destructive",
  grise: "rounded-md border border-border bg-muted px-3.5 py-2.5 text-sm",
} as const;

type Appliquer = (reglage: NonNullable<Segment["reglage"]>) => void;

/**
 * Un message traduit : le texte, et les reglages cites en liens qui les appliquent. Un
 * reglage que la page porte deja reste du texte : on ne propose pas « Oui » a qui l'a choisi.
 */
function Segments({
  segments,
  saisie,
  appliquer,
}: {
  segments: Segment[];
  saisie: Saisie;
  appliquer: Appliquer;
}) {
  return (
    <>
      {segments.map((segment, index) => {
        const { reglage } = segment;
        const cle = `${index}-${segment.texte}`;
        if (!reglage || dejaApplique(saisie, reglage))
          return <span key={cle}>{segment.texte}</span>;
        return (
          <button
            className="cursor-pointer rounded-sm font-medium underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            key={cle}
            onClick={() => appliquer(reglage)}
            type="button"
          >
            {segment.texte}
          </button>
        );
      })}
    </>
  );
}

function CarteApercu({
  rendu,
  saisie,
  appliquer,
  lienApercu,
}: {
  rendu: Rendu;
  saisie: Saisie;
  appliquer: Appliquer;
  lienApercu: string | undefined;
}) {
  const id = useId();
  const titre = useRef<HTMLHeadingElement>(null);
  const { etat, reponse, erreur, couleursDeLErreur, long } = rendu;
  // Pendant un calcul, le dernier apercu reste, grise ; une erreur le remplace. En attente
  // (une image sans rien de demande pour elle, un reglage etant invalide), la case reste vide,
  // sans « a jour ».
  const montree = reponse !== null && (etat === "pret" || etat === "calcul") ? reponse : null;

  // Le lien d'un avertissement et Reessayer disparaissent avec le rendu qui les portait : le
  // focus passe au titre de la carte plutot que de retomber sur la page.
  const appliquerPuisTitre: Appliquer = (reglage) => {
    appliquer(reglage);
    titre.current?.focus();
  };
  const relancerPuisTitre = (): void => {
    rendu.relancer();
    titre.current?.focus();
  };

  return (
    <section
      aria-busy={etat === "calcul"}
      aria-labelledby={`${id}-titre`}
      className={cn(CARTE, "flex flex-col", !montree && etat !== "erreur" && "lg:min-h-[420px]")}
    >
      <div className="mb-3.5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2
          className="rounded-sm text-[15px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
          id={`${id}-titre`}
          ref={titre}
          tabIndex={-1}
        >
          Aperçu
        </h2>
        <p className="text-[13px] text-muted-foreground" role="status">
          {etat === "calcul" ? (
            <>
              <span
                aria-hidden="true"
                className="mr-2 inline-block size-4 animate-spin rounded-full border-2 border-border border-t-foreground align-[-3px] motion-reduce:animate-none"
              />
              calcul…
            </>
          ) : etat === "pret" ? (
            <>
              <span className="hidden md:inline">
                à jour · refait tout seul après chaque réglage
              </span>
              <span className="md:hidden">touche pour agrandir</span>
            </>
          ) : null}
        </p>
      </div>

      {etat === "calcul" && long ? (
        <p className={cn(ALERTE.grise, "mb-3.5")}>
          Plus long que d'habitude : l'image est détaillée, ou un autre rendu passe avant. Le tien
          suit, sans rien à faire.
        </p>
      ) : null}

      {etat === "erreur" && erreur ? (
        <ErreurDuRendu
          appliquer={appliquerPuisTitre}
          couleursDeLErreur={couleursDeLErreur}
          erreur={erreur}
          relancer={relancerPuisTitre}
          saisie={saisie}
        />
      ) : null}

      {montree ? (
        <div className={cn("transition-opacity", etat === "calcul" && "opacity-50")}>
          <ApercuRendu
            appliquer={appliquerPuisTitre}
            lienApercu={lienApercu}
            reponse={montree}
            saisie={saisie}
          />
        </div>
      ) : etat === "erreur" ? null : (
        <div className="flex min-h-40 flex-1 items-center justify-center rounded-lg border border-dashed border-border px-6 py-10 text-center text-sm text-muted-foreground">
          {etat === "vide" ? "L'aperçu apparaît ici dès qu'une image est déposée." : null}
        </div>
      )}
    </section>
  );
}

/**
 * Un refus de l'outil, sa premiere phrase en gras (maquette `SilhouettesEtats.dc.html` :
 * « Aucun sujet trouve dans l'image. » ouvre l'encadre). Sans phrase a detacher, le
 * message reste tel quel.
 */
function PremierePhraseEnGras({
  segments,
  saisie,
  appliquer,
}: {
  segments: Segment[];
  saisie: Saisie;
  appliquer: Appliquer;
}) {
  const [tete, ...suite] = segments;
  const fin = tete && !tete.reglage ? tete.texte.indexOf(". ") : -1;
  if (!tete || fin < 0) {
    return <Segments appliquer={appliquer} saisie={saisie} segments={segments} />;
  }
  return (
    <>
      <strong className="font-semibold">{tete.texte.slice(0, fin + 1)}</strong>
      <Segments
        appliquer={appliquer}
        saisie={saisie}
        segments={[{ texte: tete.texte.slice(fin + 1) }, ...suite]}
      />
    </>
  );
}

/**
 * Ce que la carte Apercu dit d'un echec, selon la reponse de la console (tableau de la
 * relecture de #145) :
 *
 * | Reponse | Texte | Reessayer |
 * |---|---|---|
 * | 400 `rendu_refuse` | le message de l'outil, traduit, puis d'ou il vient | non |
 * | 400 autre (validation) | « Un reglage n'a pas ete accepte... » | non |
 * | 413 | l'image reste trop lourde | non |
 * | 429 `calcul_occupe` | le calcul est occupe | oui |
 * | 502 `calcul_indisponible` | le calcul n'est pas disponible sur le serveur | oui |
 * | 502 `calcul_en_panne` | le calcul a echoue sur cette image | non |
 * | 504, et `http_error` 504 | le calcul ne repond pas | oui |
 * | tout le reste (502, reseau...) | le calcul est injoignable | oui |
 */
export function ErreurDuRendu({
  erreur,
  couleursDeLErreur,
  relancer,
  saisie,
  appliquer,
}: {
  erreur: ApiError;
  couleursDeLErreur: Rendu["couleursDeLErreur"];
  relancer: () => void;
  saisie: Saisie;
  appliquer: Appliquer;
}) {
  if (erreur.status === 400 && erreur.code === "rendu_refuse") {
    // Les couleurs de la demande refusee, jamais celles de la carte : sinon le texte change
    // au premier reglage touche apres le refus, dont le clic sur le lien qu'il porte.
    const segments = traduireErreur(erreur.message, couleursDeLErreur !== null);
    return (
      <div className="flex flex-col gap-2.5">
        <p className={ALERTE.rouge} role="alert">
          <PremierePhraseEnGras appliquer={appliquer} saisie={saisie} segments={segments} />
        </p>
        <p className="text-[13px] text-muted-foreground">
          Le message vient de l'outil ; les fichiers restent désactivés.
        </p>
      </div>
    );
  }
  if (erreur.status === 400) {
    // La validation de la console : son message (en anglais, et technique) ne dit rien a
    // Nicolas, et il ne vient pas de l'outil.
    return (
      <p className={ALERTE.rouge} role="alert">
        Un réglage n'a pas été accepté : vérifie le nom et la hauteur.
      </p>
    );
  }
  if (erreur.status === 413) {
    return (
      <p className={ALERTE.rouge} role="alert">
        L'image, même réduite, reste trop lourde pour l'envoi. Exporte-la en PNG plus petit, ou en
        JPEG.
      </p>
    );
  }
  if (erreur.code === "calcul_en_panne") {
    return (
      <p className={ALERTE.grise} role="alert">
        Le calcul a échoué sur cette image (erreur interne). Réessayer ne changera rien : essaie une
        autre image.
      </p>
    );
  }
  const message =
    erreur.status === 504 ? (
      <>
        <strong className="font-semibold">Le calcul ne répond pas</strong> (rien en 25 s). Ton image
        et tes réglages sont gardés sur cette page.
      </>
    ) : erreur.status === 429 ? (
      "Le calcul est occupé, réessaie."
    ) : erreur.code === "calcul_indisponible" ? (
      "Le calcul des silhouettes n'est pas disponible sur le serveur pour l'instant."
    ) : (
      "Le calcul est injoignable pour l'instant. Réessaie dans un moment."
    );
  return (
    <div className={cn(ALERTE.grise, "flex items-center justify-between gap-3")} role="alert">
      <span className="min-w-0 flex-1">{message}</span>
      <Button className="shrink-0" onClick={relancer} size="sm" variant="outline">
        Réessayer
      </Button>
    </div>
  );
}

/** Le texte long, et sous `md` le texte court de la maquette du telephone. */
export function LongOuCourt({ long, court }: { long: string; court: string }) {
  if (long === court) return <>{long}</>;
  return (
    <>
      <span className="hidden md:inline">{long}</span>
      <span className="md:hidden">{court}</span>
    </>
  );
}

/**
 * Ce que le mode serre-livres (#337) change a l'apercu d'une figurine. Le verdict de la page
 * remplace ce que l'outil dit du serre-livre : sa puce, son avertissement (la hauteur qu'il
 * conseille peut contredire le cote du mur) et, comme la carte des filaments du jeu, la liste des
 * filaments du rendu. Les autres avertissements restent, avec leurs liens.
 */
export type ApercuSerreLivres = {
  /** Le nom de la figurine, pour le texte alternatif : jamais celui d'un fichier. */
  nom: string;
  /** Un calcul est en cours : l'apercu s'estompe, le verdict (`verdict`) non. */
  grise: boolean;
  /**
   * Le verdict et ses remedes, juste apres les puces et avant les avertissements : ce que Nicolas
   * doit lire d'abord. Le bilan du rendu et son rapport complet, plies ensemble, viennent apres.
   */
  verdict: ReactNode;
};

/** Le dernier mot sur le serre-livre, dans la forme de l'outil comme dans celle d'avant #166. */
const AVERTISSEMENT_DU_SERRE_LIVRE = /serre-livre/;

export function ApercuRendu({
  reponse,
  lienApercu,
  saisie,
  appliquer,
  serreLivres,
}: {
  reponse: RenduReponse;
  lienApercu: string | undefined;
  saisie: Saisie;
  appliquer: Appliquer;
  serreLivres?: ApercuSerreLivres | undefined;
}) {
  const { rapport } = reponse;
  const enSerreLivres = serreLivres !== undefined;
  const nom = serreLivres?.nom ?? nomDuRendu(reponse);
  const liste = filaments(rapport, rapport.contact_barre_mm !== null);
  // Sans `serre_livre`, `puces` n'ajoute pas la puce du serre-livre.
  const pourLesPuces = enSerreLivres ? { ...rapport, serre_livre: null } : rapport;
  const longues = puces(pourLesPuces);
  const courtes = pucesCourtes(pourLesPuces);
  const avertissements = enSerreLivres
    ? rapport.avertissements.filter((texte) => !AVERTISSEMENT_DU_SERRE_LIVRE.test(texte))
    : rapport.avertissements;
  const estompe = serreLivres?.grise ? "opacity-50 transition-opacity" : undefined;

  return (
    <div className={cn("flex flex-col", enSerreLivres ? "gap-3" : "gap-4")}>
      {reponse.apercu_png ? (
        <a
          className={cn(
            "block rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card",
            estompe,
          )}
          href={lienApercu}
          rel="noopener"
          target="_blank"
        >
          <img
            alt={`Aperçu de ${nom}`}
            className={
              enSerreLivres
                ? // Dans une colonne du serre-livres, l'apercu est plafonne : le verdict, dessous, doit
                  // tenir dans le premier ecran (decision de Nicolas, le 5 octobre 2026).
                  "mx-auto block h-auto max-h-52 w-auto max-w-full rounded-md border border-border bg-white"
                : "h-auto w-full rounded-md border border-border bg-white"
            }
            src={`data:image/png;base64,${reponse.apercu_png}`}
          />
        </a>
      ) : null}
      {/* Au telephone, l'apercu s'ouvre en grand : la maquette le dit sous l'image. */}
      {enSerreLivres && reponse.apercu_png ? (
        <p className="-mt-2 text-right text-[13px] text-muted-foreground md:hidden">
          touche pour agrandir
        </p>
      ) : null}

      <ul className={cn("flex flex-wrap gap-2", estompe)} aria-label="Chiffres du rendu">
        {enSerreLivres
          ? // Les puces courtes, comme les planches du bureau : toutes tiennent sur une ligne.
            courtes.map((puce) => (
              <li className="rounded-full bg-muted px-2.5 py-1 text-[13.5px]" key={puce}>
                {puce}
              </li>
            ))
          : longues.map((puce, index) => (
              <li className="rounded-full bg-muted px-2.5 py-1 text-[13.5px]" key={puce}>
                <LongOuCourt court={courtes[index] ?? puce} long={puce} />
              </li>
            ))}
      </ul>

      {/* Le verdict d'abord (le mode serre-livres) : avant ce que l'outil avertit. */}
      {serreLivres?.verdict}

      {avertissements.length > 0 ? (
        <ul
          className={cn(ALERTE.jaune, "flex flex-col gap-1.5", estompe)}
          aria-label="Avertissements de l'outil"
        >
          {avertissements.map((avertissement) => (
            <li key={avertissement}>
              <Segments appliquer={appliquer} saisie={saisie} segments={traduire(avertissement)} />
            </li>
          ))}
        </ul>
      ) : null}

      {enSerreLivres ? (
        // Un seul bloc, ferme : ce que le rendu a fait, et son rapport complet. La colonne montre
        // d'abord l'apercu, les puces et le verdict (decision de Nicolas, le 5 octobre 2026).
        <details className={cn("group rounded-md border border-border", estompe)} data-bilan="">
          <summary className="min-h-11 cursor-pointer rounded-md px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:min-h-9">
            <span className="font-medium">Ce que le rendu a fait</span>
            <span className="block text-[13px] text-muted-foreground">et rapport complet</span>
          </summary>
          <div className="flex flex-col gap-3 border-t border-border px-3 py-2.5">
            <div className={cn(NOTE, "flex flex-col gap-0.5")}>
              {bilan(rapport).map((ligne) => (
                <p key={ligne}>{ligne}</p>
              ))}
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Rapport complet
              </p>
              <RapportTexte
                appliquer={appliquer}
                className="overflow-x-auto rounded-md border border-border px-3 py-2.5 font-mono text-xs leading-relaxed"
                reponse={reponse}
                saisie={saisie}
              />
            </div>
          </div>
        </details>
      ) : (
        <>
          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <LongOuCourt court={titreFilaments(rapport, true)} long={titreFilaments(rapport)} />
              </p>
              {liste.length > 0 ? (
                <ol className="flex flex-col">
                  {liste.map((filament) => (
                    <li
                      className="grid grid-cols-[22px_20px_minmax(0,1fr)_auto] items-center gap-2 py-1.5 text-sm"
                      key={filament.numero}
                    >
                      <span className="font-semibold text-muted-foreground">{filament.numero}</span>
                      <span
                        aria-hidden="true"
                        className="size-[18px] rounded border border-border"
                        style={{ backgroundColor: filament.hexa }}
                      />
                      <span>{filament.nom}</span>
                      {/* Le telephone ne garde que le numero, la pastille et le nom (maquette). */}
                      <span className="hidden text-[13px] text-muted-foreground md:inline">
                        {filament.detail}
                      </span>
                    </li>
                  ))}
                </ol>
              ) : null}
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Ce que le rendu a fait
              </p>
              <div className={cn(NOTE, "flex flex-col gap-0.5")}>
                {bilan(rapport).map((ligne) => (
                  <p key={ligne}>{ligne}</p>
                ))}
              </div>
            </div>
          </div>

          <details aria-label="Rapport complet" className="group rounded-md border border-border">
            <summary className="cursor-pointer rounded-md px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              Rapport complet
            </summary>
            <RapportTexte
              appliquer={appliquer}
              className="overflow-x-auto border-t border-border px-3 py-2.5 font-mono text-xs leading-relaxed"
              reponse={reponse}
              saisie={saisie}
            />
          </details>
        </>
      )}
    </div>
  );
}

/** Le rapport complet du rendu : le texte de l'outil, avec les reglages qu'il cite en liens. */
function RapportTexte({
  reponse,
  saisie,
  appliquer,
  className,
}: {
  reponse: RenduReponse;
  saisie: Saisie;
  appliquer: Appliquer;
  className: string;
}) {
  return (
    <pre className={className}>
      {reponse.texte.split("\n").map((ligne, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: les lignes du rapport sont fixes, et deux peuvent se ressembler
        <span className="block" key={index}>
          {/* La ligne « Image : » porte le nom du fichier depose : rien a y traduire. */}
          {/^Image\s+:/.test(ligne) ? (
            ligne
          ) : (
            <Segments appliquer={appliquer} saisie={saisie} segments={traduire(ligne)} />
          )}
        </span>
      ))}
    </pre>
  );
}

/** Le nom des fichiers du rendu affiche : celui du 3MF, sans son extension. */
function nomDuRendu(reponse: RenduReponse): string {
  const troisMf = reponse.fichiers.find((fichier) => fichier.nom.endsWith(".3mf"));
  return troisMf ? troisMf.nom.replace(/\.3mf$/, "") : "silhouette";
}

function CarteFichiers({
  reponse,
  liens,
  aJour,
}: {
  reponse: RenduReponse | null;
  liens: Record<string, string>;
  aJour: boolean;
}) {
  const id = useId();
  const nom = reponse ? nomDuRendu(reponse) : null;
  const troisMf = reponse?.fichiers.find((fichier) => fichier.nom.endsWith(".3mf"));
  const zip = reponse?.fichiers.find((fichier) => fichier.nom.endsWith(".zip"));

  return (
    <section aria-labelledby={`${id}-titre`} className={CARTE}>
      <div className="mb-3.5 hidden md:block">
        <TitreCarte id={`${id}-titre`}>Fichiers</TitreCarte>
      </div>
      <div className="grid grid-cols-2 gap-2.5 md:flex md:flex-wrap">
        <LienFichier
          href={aJour && troisMf ? liens[troisMf.nom] : undefined}
          nom={troisMf?.nom}
          principal
        >
          <span aria-hidden="true">{FLECHE_BAS}</span> Télécharger{" "}
          {troisMf ? troisMf.nom : "le 3MF"}
        </LienFichier>
        <LienFichier href={aJour && zip ? liens[zip.nom] : undefined} nom={zip?.nom}>
          <LongOuCourt court="STL (zip)" long="STL par couleur (zip)" />
        </LienFichier>
        <LienFichier
          href={aJour && nom ? liens.apercu : undefined}
          nom={nom ? `${nom}-apercu.png` : undefined}
        >
          <LongOuCourt court="Aperçu" long="Aperçu (PNG)" />
        </LienFichier>
      </div>
      {reponse ? (
        <p className={cn(NOTE, "mt-3.5 hidden md:block")}>
          Double-clique le 3MF : Bambu Studio l'ouvre en un seul objet, une pièce par couleur,
          chacune déjà sur son filament. Il reste à mettre la bonne bobine en face de chaque numéro.
        </p>
      ) : null}
    </section>
  );
}

/**
 * Un lien de telechargement, ou sa forme eteinte : sans `href` (donc hors de l'ordre de
 * tabulation) et `aria-disabled`, tant qu'il n'y a rien d'a jour a telecharger.
 */
function LienFichier({
  href,
  nom,
  principal = false,
  children,
}: {
  href: string | undefined;
  nom: string | undefined;
  principal?: boolean;
  children: ReactNode;
}) {
  const forme = cn(
    "inline-flex h-10 items-center justify-center gap-2 rounded-md border px-3.5 text-sm font-medium",
    principal && "col-span-2",
  );
  if (!href || !nom) {
    return (
      // biome-ignore lint/a11y/useValidAnchor: un lien eteint n'a volontairement pas de cible (plan #145) ; aria-disabled et role le disent, et sans href il sort de l'ordre de tabulation
      <a
        aria-disabled="true"
        className={cn(
          forme,
          "cursor-default",
          principal
            ? "border-transparent bg-muted-foreground/30 text-muted-foreground"
            : "border-border bg-muted/50 text-muted-foreground",
        )}
        role="link"
      >
        {children}
      </a>
    );
  }
  return (
    <a
      className={cn(
        forme,
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card",
        principal
          ? "border-primary bg-primary text-primary-foreground hover:opacity-90"
          : "border-input bg-card text-foreground hover:bg-muted",
      )}
      download={nom}
      href={href}
    >
      {children}
    </a>
  );
}

/** Un lien local vers le fichier choisi (la vignette), revoque au changement d'image. */
export function useUrlLocale(fichier: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!fichier) {
      setUrl(null);
      return;
    }
    const lien = URL.createObjectURL(fichier);
    setUrl(lien);
    return () => URL.revokeObjectURL(lien);
  }, [fichier]);
  return url;
}
